/** iOS-style segmented control: a gray track with a white thumb that slides to the picked option. */
import { cn } from '../../utils/cn';

export interface SegmentedControlProps<T extends string | number> {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  label: string;
  className?: string;
}

export function SegmentedControl<T extends string | number>({ value, options, onChange, label, className }: SegmentedControlProps<T>) {
  const picked = Math.max(0, options.findIndex((o) => o.value === value));
  return (
    <div role="group" aria-label={label}
      className={cn('relative inline-grid auto-cols-fr grid-flow-col rounded-[9px] bg-gray-100 p-0.5', className)}>
      {/* Thumb width is one column of the padded track; translateX(n·100%) moves it n columns. */}
      <span aria-hidden
        className="absolute inset-y-0.5 left-0.5 rounded-[7px] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.1),0_0_0_0.5px_rgba(0,0,0,0.05)] motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out"
        style={{ width: `calc((100% - 4px) / ${options.length})`, transform: `translateX(${picked * 100}%)` }} />
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}
          className={cn(
            'relative min-w-12 rounded-[7px] px-3 py-1 text-[13px] font-medium tabular-nums transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
            o.value === value ? 'text-gray-900' : 'text-gray-600 hover:text-gray-800',
          )}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
